import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LucideSymbolIcon from '../../components/icons/LucideSymbolIcon';
import PasswordRecoveryLayout, {
  AUTH_FIELD_CLASS,
  RecoveryAlert,
  RecoveryPrimaryButton,
} from '../../components/auth/PasswordRecoveryLayout';
import { resetPasswordWithToken } from '../../services/api';

const MIN_LEN = 8;

function RecoveryPasswordField({
  id,
  label,
  value,
  onChange,
  placeholder,
  showPassword,
  onToggleShow,
  withToggle = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  showPassword: boolean;
  onToggleShow: () => void;
  withToggle?: boolean;
}) {
  return (
    <label className="block space-y-1" htmlFor={id}>
      <span className="text-xs font-bold text-on-surface-variant">{label}</span>
      <div className="relative rounded-lg border border-transparent focus-glow">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          <LucideSymbolIcon name="lock" tone="muted" size={18} />
        </span>
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          className={`${AUTH_FIELD_CLASS} ${withToggle ? 'pr-10 sm:pr-11' : ''}`}
          value={value}
          onChange={(ev) => onChange(ev.target.value)}
          placeholder={placeholder}
        />
        {withToggle ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-public-btn p-1 text-on-surface-variant transition hover:text-primary"
            onClick={onToggleShow}
            aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          >
            <LucideSymbolIcon name={showPassword ? 'visibility_off' : 'visibility'} tone="inherit" size={18} />
          </button>
        ) : null}
      </div>
    </label>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const canSubmit =
    token.length > 0 &&
    password.length >= MIN_LEN &&
    confirm.length >= MIN_LEN &&
    password === confirm;

  const passwordMismatch = confirm.length >= MIN_LEN && password !== confirm;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      await resetPasswordWithToken({
        token,
        nouveauMotdepasse: password,
        confirmMotdepasse: confirm,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lien invalide ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <PasswordRecoveryLayout
        activeStep={2}
        compact
        compactTopAlign
        eyebrow="Étape manquante"
        title="Lien incomplet"
        description="Demandez un code depuis « Mot de passe oublié », puis saisissez-le sur la page suivante."
        backLink={{ label: 'Retour à la connexion', to: '/login' }}
        showStepper={false}
      >
        <RecoveryPrimaryButton type="button" onClick={() => navigate('/forgot-password')}>
          <>
            Nouvelle demande
            <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="text-white" />
          </>
        </RecoveryPrimaryButton>
      </PasswordRecoveryLayout>
    );
  }

  if (success) {
    return (
      <PasswordRecoveryLayout
        activeStep={3}
        compact
        completed
        title="Mot de passe mis à jour"
        description="Vous pouvez maintenant vous connecter avec votre nouveau mot de passe."
        backLink={{ label: 'Retour à la connexion', to: '/login' }}
      >
        <div className="space-y-3">
          <RecoveryAlert variant="success">
            Votre mot de passe a été enregistré avec succès.
          </RecoveryAlert>
          <RecoveryPrimaryButton type="button" onClick={() => navigate('/login')}>
            <>
              Se connecter
              <LucideSymbolIcon name="login" tone="inherit" size={18} className="text-white" />
            </>
          </RecoveryPrimaryButton>
        </div>
        <div className="h-28 shrink-0 sm:h-32" aria-hidden />
      </PasswordRecoveryLayout>
    );
  }

  return (
    <PasswordRecoveryLayout
      activeStep={3}
      compact
      compactTopAlign
      title="Nouveau mot de passe"
      description="Choisissez un mot de passe d'au moins 8 caractères."
      backLink={{ label: 'Annuler et revenir à la connexion', to: '/login' }}
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
        <div className="space-y-1">
          <RecoveryPasswordField
            id="rp-pw"
            label="Nouveau mot de passe"
            value={password}
            onChange={setPassword}
            placeholder="Minimum 8 caractères"
            showPassword={showPassword}
            onToggleShow={() => setShowPassword((v) => !v)}
            withToggle
          />
          <p
            className={[
              'text-[11px] font-semibold sm:text-xs',
              password.length >= MIN_LEN ? 'text-emerald-700' : 'text-on-surface-variant',
            ].join(' ')}
          >
            {password.length}/{MIN_LEN} caractères minimum
          </p>
        </div>

        <div className="space-y-1">
          <RecoveryPasswordField
            id="rp-cf"
            label="Confirmation"
            value={confirm}
            onChange={setConfirm}
            placeholder="Répétez le mot de passe"
            showPassword={showPassword}
            onToggleShow={() => setShowPassword((v) => !v)}
            withToggle
          />
          {passwordMismatch ? (
            <p className="text-[11px] font-semibold text-red-600 sm:text-xs">
              Les mots de passe ne correspondent pas.
            </p>
          ) : null}
        </div>

        {error ? <RecoveryAlert variant="error">{error}</RecoveryAlert> : null}

        <p className="text-[11px] leading-snug text-on-surface-variant sm:text-xs">
          Utilisez des lettres, chiffres et symboles pour renforcer la sécurité.
        </p>

        <RecoveryPrimaryButton loading={loading} disabled={!canSubmit || loading}>
          {loading ? 'Enregistrement…' : 'Enregistrer le nouveau mot de passe'}
        </RecoveryPrimaryButton>
      </form>
    </PasswordRecoveryLayout>
  );
}
